import { useContext, useState, useEffect, useRef } from "react";

//Components
import AvailableOfficerTable from "./AvailableOfficerTable";
import ActiveIncidentTableContainer from "./ActiveIncidentTableContainer/ActiveIncidentTableContainer";
import PaginatedTableWrapper from "./PaginatedTableWrapper";
//Contexts
import { UserContext } from "../Contexts/UserContext";
import { DashboardContext } from "../Contexts/DashboardContext";
import { SetupContext } from "../Contexts/SetupContext";

//Fetching
import { useLazyQuery } from "@apollo/client";

//Utils
import {
  inactiveIncidentHeaders,
  parseInactiveIncidents,
} from "../utils/InactiveIncidentHelper";
import { localStoreData, parseObjectArrayIdString } from "../utils/utils";
import { getInactiveIncidentVariables } from "../utils/dashboardQueryUtils";
import { extractGraphqlArrayPayload } from "../utils/arrayPayloadUtils";

//Styles
import { useStyles } from "../Styles/styles";
import moment from "moment";

import { ACTIVE_INCIDENT_QUERY } from "../Queries/ActiveIncidentQuery";
import { ACTIVE_INCIDENTS_ACTIONS } from "../Actions/ActiveIncidentsActions";
import { AVAILABLE_OFFICERS_QUERY } from "../Queries/AvailableOfficersQuery";
import CountdownTimer from "./CountdownTimer/CountdownTimer";

const Dashboard = ({
  handleIncidentOpening,
  handlePersonOpening,
  handleTicketOpening,
  setPopper,
  pausePolling,
  bulletinOpen,
  bulletinSize,
  userPreferences,
  timestamp,
  ...props
}) => {
  const classes = useStyles();
  const { user, responseErrorHandler, setUser, onlineState } =
    useContext(UserContext);
  const { setup, checkForSetup } = useContext(SetupContext);
  const {
    setAvailOfficer,
    showActiveIncidents,
    getInactiveIncidents,
    inactiveIncidentsData,
    inactiveIncidentsLoading,
    inactiveIncidentsError,
    activeIncidentDispatch,
    availableOfficerDispatch,
    _dashboardIsMounted,
  } = useContext(DashboardContext);
  const { SET_STATE } = ACTIVE_INCIDENTS_ACTIONS;

  const [setupReady, setSetupReady] = useState(false);
  const clientTimeout = +user?.inactivityTimeout || 1800;

  useEffect(() => {
    return () => {
      // ComponentWillUnmount in Class Component
      _dashboardIsMounted.current = false;
    };
  }, [_dashboardIsMounted]);

  useEffect(() => {
    let ready = checkForSetup();
    if (ready) {
      setSetupReady(true);
    } else {
      checkForSetup();
    }
  }, [checkForSetup]);

  const [
    getActiveIncidents,
    {
      loading: activeIncidentsLoading,
      error: activeIncidentsError,
      called: activeIncidentsCalled,
    },
  ] = useLazyQuery(ACTIVE_INCIDENT_QUERY, {
    variables: { originatorId: user?.id, homeOriId: user?.homeOriId },
    fetchPolicy: "network-only",
    context: { serializationKey: "incidentQueue" },
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const payload = extractGraphqlArrayPayload(data, "activeIncidents");
        if (Array.isArray(payload)) {
          activeIncidentDispatch({
            type: SET_STATE,
            payload,
          });
        }
      }
    },
  });

  const [
    getAvailableOfficers,
    {
      loading: availableOfficersLoading,
      error: availableOfficersError,
      refetch: availableOfficersRefetch,
      called: availableOfficersCalled,
    },
  ] = useLazyQuery(AVAILABLE_OFFICERS_QUERY, {
    context: { serializationKey: "incidentQueue" },
    variables: { oriIds: user?.selectedOris, originatorId: user?.id },
    fetchPolicy: "no-cache",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const payload = extractGraphqlArrayPayload(data, "availableOfficers");
        if (Array.isArray(payload)) {
          availableOfficerDispatch({
            type: SET_STATE,
            payload,
          });
        }
        if (Array.isArray(payload) && payload.length > 0) {
          payload.forEach(o => {
            if (o?.id?.toString() === user?.id.toString()) {
              //if the user is on duty but their latest status is off duty
              if (user?.onDuty !== o?.deptAttribute?.onDuty) {
                setUser(prevState => {
                  return { ...prevState, onDuty: o?.deptAttribute?.onDuty };
                });
              }
            }
          });
        }
      }
    },
  });

  useEffect(() => {
    document.title = `Valcour Mobile - Dashboard`;
    if (user?.id) {
      getActiveIncidents({
        variables: {
          originatorId: user?.id,
          noSessionReset: true,
          oriIds: parseObjectArrayIdString(user?.selectedOrisObjs),
          homeOriId: user?.homeOriId,
        },
      });
      getAvailableOfficers({
        variables: {
          originatorId: user?.id,
          noSessionReset: true,
          oriIds: parseObjectArrayIdString(user?.selectedOrisObjs),
          homeOriId: user?.homeOriId,
        },
      });
    }
  }, [
    getActiveIncidents,
    getAvailableOfficers,
    user?.homeOriId,
    user?.id,
    user?.selectedOrisObjs,
  ]);

  useEffect(() => {
    if (import.meta.env.VITE_USE_POLLING.toLowerCase() === "true") {
      const interval = setInterval(() => {
        if (
          onlineState &&
          user?.isAuthenticated &&
          user?.id &&
          _dashboardIsMounted &&
          !pausePolling &&
          showActiveIncidents &&
          document.visibilityState === "visible"
        ) {
          getActiveIncidents({
            variables: {
              originatorId: user?.id,
              noSessionReset: true,
              oriIds: parseObjectArrayIdString(user?.selectedOrisObjs),
              homeOriId: user?.homeOriId,
            },
          });
          getAvailableOfficers({
            variables: {
              originatorId: user?.id,
              noSessionReset: true,
              oriIds: parseObjectArrayIdString(user?.selectedOrisObjs),
              homeOriId: user?.homeOriId,
            },
          });
        }
      }, 12000);
      return () => {
        clearInterval(interval);
      };
    }
  });

  // *************************************************************************************

  const dashRef = useRef(null);

  const [dashSize, setDashSize] = useState({
    width: window.innerWidth,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      dashRef.current &&
        setDashSize({
          width: dashRef.current.offsetWidth,
          height: dashRef.current.offsetHeight,
        });
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const storeUserData = async () => {
      try {
        let localUser = { ...user };
        await localStoreData(localUser, "mp_user", false);
      } catch (error) {
        console.error("Error storing user data:", error);
      }
    };

    storeUserData();
  }, [user]);
  const handleGetInactiveIncidents = (perPage, page) => {
    getInactiveIncidents({
      variables: getInactiveIncidentVariables(user, perPage, page),
    });
  };

  if (
    activeIncidentsLoading &&
    (!activeIncidentsCalled || !setupReady) &&
    onlineState
  )
    return (
      <p>
        {activeIncidentsLoading || !activeIncidentsCalled
          ? "Loading Dashboard..."
          : "Loading setup list data"}
      </p>
    );
  if (activeIncidentsError)
    return <p>Error: {JSON.stringify(activeIncidentsError)}</p>;

  return (
    <div
      id="dashboard-wrapper"
      style={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      {/* <Bulletins
        handleDrawerClose={() => setBulletinToggle(prevState => !prevState)}
        open={bulletinToggle}
        // height={height} 
        /> */}
      <div style={{ height: "100%" }} className={classes.content} ref={dashRef}>
        {showActiveIncidents ? (
          <ActiveIncidentTableContainer
            dashSize={dashSize}
            handleIncidentOpening={handleIncidentOpening}
            // data={activeIncidentsState}
            activeIncidentsCalled={activeIncidentsCalled}
            loading={activeIncidentsLoading}
            error={activeIncidentsError}
            logCodes={setup?.setupLists?.logCodes || []}
            oriIds={
              user?.selectedOris
                ? user?.selectedOris
                : user?.homeOriId?.toString()
            }
            tableWidth={bulletinSize[bulletinOpen]}
            userPreferences={userPreferences}
          />
        ) : (
          <PaginatedTableWrapper
            dashSize={dashSize}
            tableHeight={dashRef?.current?.offsetHeight * 0.95}
            tableName={"Inactive Incidents"}
            headers={inactiveIncidentHeaders}
            userId={user?.id}
            paginatedQueryHandler={handleGetInactiveIncidents}
            rows={
              inactiveIncidentsData
                ? parseInactiveIncidents(
                    inactiveIncidentsData[
                      Object.keys(inactiveIncidentsData)[0]
                    ],
                    handleIncidentOpening,
                    user?.roleNames,
                  )
                : []
            }
            loading={inactiveIncidentsLoading}
            error={inactiveIncidentsError}
            paginated={true}
            tableWidth={bulletinSize[bulletinOpen]}
          />
        )}
        {showActiveIncidents && (
          <AvailableOfficerTable
            availableOfficersCalled={availableOfficersCalled}
            showActiveIncidents={showActiveIncidents}
            dashSize={dashSize}
            user={user}
            loading={availableOfficersLoading}
            error={availableOfficersError}
            refetch={availableOfficersRefetch}
            setAvailOfficer={setAvailOfficer}
            tableWidth={bulletinSize[bulletinOpen]}
            userPreferences={userPreferences}
          />
        )}
      </div>
      <div
        style={{
          backgroundColor: "#303e4f",
          color: "white",
          textAlign: "center",
          padding: "3px 20px",
          fontFamily: "sans-serif",
          fontSize: "0.8rem",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ flex: 1 }} />
        <div style={{ flex: 1 }}>
          &copy; {moment().local().format("YYYY")} - CrossWind Technologies, Inc
          (Build: {props.currentVersionDate})
        </div>
        <CountdownTimer
          lastActiveTimestamp={timestamp}
          sessionLength={+clientTimeout}
        />
      </div>
    </div>
  );
};

export default Dashboard;
